/// <reference lib="webworker" />
// import type { ServiceMethods, Services } from './services';
export class ServiceError {
	public code: string;
	public message?: string;

	constructor(code: string, message?: string) {
		this.code = code;
		this.message = message;
	}
}
export type ResponseWithHeaders<T> = T & {
	/**
	 * Headers.
	 */
	headers: { [k: string]: string };
};

/**
 * @description RequestData.
 */
export interface RequestData {
	// biome-ignore lint/suspicious/noExplicitAny: <biome-ignore>
	headers?: any;

	// biome-ignore lint/suspicious/noExplicitAny: <biome-ignore>
	query?: any;

	// biome-ignore lint/suspicious/noExplicitAny: <biome-ignore>
	body?: any;
}

// biome-ignore lint/suspicious/noExplicitAny: <biome-ignore>
export interface ResponseService<Data = any | null> {
	data?: Data;

	result: ResponseResult;
}

export interface ResponseResult {
	readonly timestamp?: Date;

	status: number;

	code: string;

	message: string;
}

// export interface JSONApiResponse<T> {
// 	value(): Promise<ResponseWithHeaders<T>>;
// }

// export const tokenExpiredError = ['OAUT.02999'];
//
export class CurrentRequests {
	abortController: AbortController;

	service: Service;

	method: Method;

	type: QueueRequestType;

	constructor(
		service: Service,
		method: Method,
		type: QueueRequestType,
		abortController: AbortController,
	) {
		this.type = type;
		this.method = method;
		this.service = service;
		this.abortController = abortController;
	}
}

export class CurrentFileRequests {
	abortController: AbortController;

	path: string;

	type: QueueRequestFileType;

	constructor(
		path: string,
		type: QueueRequestFileType,
		abortController: AbortController,
	) {
		this.type = type;
		this.path = path;

		this.abortController = abortController;
	}
}

export interface ApiPostMessage {
	TOKEN_IS_REFRESHED?: ResponseWithHeaders<AuthTokenResult>;

	TOKEN_IS_NOT_REFRESHED?: ResponseWithHeaders<AuthTokenResult>;
}

export interface ApiQueueServicePostMessage {
	uuid: string;
}

export class TokenServiceError extends ServiceError {
	errorResult: ResponseResult;

	constructor(code: string, errorResult: ResponseResult, message?: string) {
		super(code, message);
		this.errorResult = errorResult;
	}
}

export type QueueRequestStatus = 'success' | 'error' | 'pending';

export enum QueueRequestType {
	default = 'default',
	pendingTokenRefresh = 'pendingTokenRefresh',
	pendingNetworkConnection = 'pendingNetworkConnection',
}

export enum QueueRequestFileType {
	default = 'default',

	pendingNetworkConnection = 'pendingNetworkConnection',
}

export enum AbortReason {
	connectionIsOffline = 'connectionIsOffline',
	tokenError = 'tokenError',
	tokenIsNotRefreshed = 'tokenIsNotRefreshed',
	isLogout = 'isLogout',
	globalError = 'globalError',
}

export enum AbortReasonFile {
	connectionIsOffline = 'connectionIsOffline',
	timeout = 'timeout',
}

/**
 * @description QueueRequest.
 */
export interface QueueRequest<
	// biome-ignore lint/suspicious/noExplicitAny: <biome-ignore>
	Request extends (...args: any[]) => Promise<ReturnType<Request>>,
> {
	service: Service;

	method: Method;

	request: Request;

	status: QueueRequestStatus;

	type: QueueRequestType;

	response: Response | null;

	abortReason?: AbortReason;
}

export interface QueueFileRequest<
	// biome-ignore lint/suspicious/noExplicitAny: <biome-ignore>
	Request extends (...args: any[]) => Promise<ReturnType<Request>>,
> {
	request: Request;

	status: QueueRequestStatus;

	type: QueueRequestFileType;

	response: Response | null;

	abortReason?: AbortReasonFile;
}

export type TokenRefreshStatus =
	| 'TOKEN_IS_NOT_REFRESHED'
	| 'TOKEN_IS_REFRESHED'
	| null;

export type TokenRefreshData = {
	status: TokenRefreshStatus;

	response: AuthTokenResult | null;
};

/**
 *
 */
export interface OAuth2AccessToken {
	expiresIn?: number;

	tokenType?: string;

	scope?: string;

	additionalInformation?: object;

	expiration?: Date;

	expired?: boolean;
}

export interface AuthTokenResult {
	result: ResponseResult;

	data: OAuth2AccessToken;
}

export interface ServiceWorkerMessagePostMessage<
	Message extends string,
	MessageData = any,
> {
	message: Message;

	messageData?: MessageData;
}

interface SWStatus {
	activate?: string;
	install?: string;
}

export interface ServiceWorkerOutputMessage {
	meta?: 'workbox-broadcast-update';

	app?:
		| ServiceWorkerMessagePostMessage<
				'hasRequestsWithNetworkProblemChanged',
				boolean
		  >
		| ServiceWorkerMessagePostMessage<
				'tokenRefreshDataChanged',
				{
					status: TokenRefreshStatus;
					response: AuthTokenResult;
				}
		  >
		| ServiceWorkerMessagePostMessage<'globalErrorChanged', ServiceError>
		| ServiceWorkerMessagePostMessage<'refreshToken'>
		| ServiceWorkerMessagePostMessage<'updateCache'>
		| ServiceWorkerMessagePostMessage<'mfModuleIsFailedLoad', string>
		| ServiceWorkerMessagePostMessage<'swStatus', SWStatus>
		| ServiceWorkerMessagePostMessage<'logout'>
		| ServiceWorkerMessagePostMessage<'login'>
		| ServiceWorkerMessagePostMessage<'setActiveTabId', string>;
}

export interface GlobalErrors {
	allErrors: Record<string, string>;
	tokenErrors: Record<string, string>;
	loginErrors: Record<string, string>;
	notBlockingApp: Record<string, string>;
}

export type InitData = {
	appManifestUrl: string;
};

export interface ServiceWorkerInputMessage {
	serviceWorker?:
		| ServiceWorkerMessagePostMessage<'networkIsOffline'>
		| ServiceWorkerMessagePostMessage<'networkIsOnline'>
		| ServiceWorkerMessagePostMessage<'retryRequests'>
		| ServiceWorkerMessagePostMessage<'retryRequestsInBackground'>
		| ServiceWorkerMessagePostMessage<'clearData'>
		| ServiceWorkerMessagePostMessage<'initFileStore', InitData>
		| ServiceWorkerMessagePostMessage<
				'initServiceStore',
				{
					globalErrors: GlobalErrorsJson;
					requestWithoutAuthConfig: {
						[ServiceName in keyof Record<string, string>]?: Array<
							keyof Record<string, string>[ServiceName]
						>;
					};
				}
		  >
		| ServiceWorkerMessagePostMessage<'logout'>
		| ServiceWorkerMessagePostMessage<'login'>
		| ServiceWorkerMessagePostMessage<'setActiveTabId', string>;
}

interface Browser {
	name: string;

	version: string;
}

interface Coordinates {
	latitude: string;

	longitude: string;

	timeZone: string;

	country: string;

	region: string;
}

enum DeviceTypes {
	mobile = 'mobile',
	desktop = 'desktop',
	tablet = 'tablet',
}

export interface DeviseInfoData {
	readonly coordinates: Coordinates;

	readonly browser: Browser;

	readonly os: string;

	readonly deviceType: DeviceTypes;
}

export enum CacheNames {
	js = 'js',
	css = 'css',
	html = 'html',
	json = 'json',
	wasm = 'wasm',
	images = 'images',
	fonts = 'fonts',
}
export type CacheNamesKeys = keyof typeof CacheNames;
export type Service = keyof Record<string, string> | 'unknown';
export type Method = keyof Record<string, string> | 'unknown';
export interface GlobalErrorsJson {
	otherErrors: Record<string, string>;
	tokenErrors: Record<string, string>;
	loginErrors: Record<string, string>;
	notBlockingApp: Record<string, string>;
}
